/*! Copyright (c) Safe As Milk. All rights reserved. */
import Cart from "cart-store";

class FreeShippingBar extends HTMLElement {
  #animationInterval = null;
  #boundOrchestrateModalEvent;
  #parentModal;
  #playingAnimation;

  constructor() {
    super();
    this.#boundOrchestrateModalEvent = this.#orchestrateModalEvent.bind(this);
  }

  async connectedCallback() {
    this.amountToSpendElement = this.querySelector("cart-amount-to-spend");
    this.amountLeftElement = this.querySelector("cart-amount-left");
    this.minimumReachedElement = this.querySelector("cart-minimum-reached");
    this.track = this.querySelector("cart-discount-qty-bar-track");
    this.goalAnimation = null;
    this.#playingAnimation = false;

    this.minimumValue = Number(this.getAttribute("minimum-value"));
    this.value = Number(this.getAttribute("value"));

    this.#parentModal = this.closest("modal-dialog, popup-dialog");
    this.#setVariables();

    // ✅ Subscribe to cart updates
    this.unsubscribe = Cart.subscribe((state) => {
      const newValue = state.cart.item_count;

      if (this.value !== newValue) {
        this.value = newValue;
        this.#update();
      }

      if (this.value >= this.minimumValue) {
        this.track.addEventListener(
          "transitionend",
          () => setTimeout(() => this.#playGoalAnimation(), 250),
          { once: true }
        );
      }
    });

    this.revealDelay = this.getAttribute("reveal-delay") || 0;

    // ✅ Handle modal visibility or scroll reveal
    if (this.#parentModal) {
      this.#parentModal.addEventListener("on:modal:opened", this.#boundOrchestrateModalEvent);
      this.#parentModal.addEventListener("on:modal:closed", this.#boundOrchestrateModalEvent);
    } else {
      const options = { rootMargin: "0px", threshold: 0.95 };
      this.visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          setTimeout(() => {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-visible");
              setTimeout(() => {
                if (this.amountLeft === 0) this.#playGoalAnimation();
              }, 500);
              this.visibilityObserver.disconnect();
            }
          }, this.revealDelay);
        });
      }, options);
      this.visibilityObserver.observe(this);
    }

    // ✅ Load animation file (lazy)
    const goalAnimationElement = this.querySelector("cart-discount-qty-bar-goal-animation");
    if (goalAnimationElement) {
      const { DotLottie } = await import("dotlottie");
      const loadAnimation = () => {
        this.goalAnimation = new DotLottie({
          canvas: goalAnimationElement.querySelector("canvas"),
          loop: false,
          autoplay: false,
          src: goalAnimationElement.getAttribute("data-url"),
        });
      };
      if (this.#parentModal) {
        this.#parentModal.on("on:modal:opening", loadAnimation, { once: true });
      } else {
        loadAnimation();
      }
    }
  }

  disconnectedCallback() {
    this.classList.remove("is-visible");
    if (this.visibilityObserver) this.visibilityObserver.disconnect();
    if (this.#parentModal) {
      this.#parentModal.removeEventListener("on:modal:opened", this.#boundOrchestrateModalEvent);
      this.#parentModal.removeEventListener("on:modal:closed", this.#boundOrchestrateModalEvent);
    }
    if (this.unsubscribe) this.unsubscribe();
    if (this.#animationInterval) clearInterval(this.#animationInterval);
  }

  #setVariables() {
    this.amountLeft = Math.max(this.minimumValue - this.value, 0);
    this.displacement = Math.round((this.value / this.minimumValue) * 100);
  }

  #update() {
    this.setAttribute("value", this.value);
    this.#setVariables();

    // ✅ Update bar fill
    this.track.style.setProperty("--displacement", `calc(100% - ${this.displacement}%)`);
    if  (this.displacement >= '100') {
      startConfetti();
    }
    // ✅ Update text dynamically
    if (this.amountLeft > 0) {
      const plural = this.amountLeft > 1 ? "items" : "item";
      this.amountLeftElement.textContent = `Pick any ${this.amountLeft} more ${plural} and save 17.76%`;
    }

    // ✅ Visibility management
    if (this.value === 0) {
      this.amountToSpendElement.removeAttribute("hidden");
      this.amountLeftElement.setAttribute("hidden", "");
      this.minimumReachedElement.setAttribute("hidden", "");
    } else if (this.amountLeft > 0) {
      this.amountToSpendElement.setAttribute("hidden", "");
      this.amountLeftElement.removeAttribute("hidden");
      this.minimumReachedElement.setAttribute("hidden", "");
    } else {
      this.amountToSpendElement.setAttribute("hidden", "");
      this.amountLeftElement.setAttribute("hidden", "");
      this.minimumReachedElement.removeAttribute("hidden");
    }
  }

  #playGoalAnimation() {
    const goalAnimationElement = this.querySelector("cart-discount-qty-bar-goal-animation");
    const animationShown = Boolean(localStorage.getItem("cartDiscountAnimationShown"));
    if (!goalAnimationElement || animationShown || this.#playingAnimation) return;
    this.#playingAnimation = true;

    this.#animationInterval = setInterval(() => {
      if (this.goalAnimation && this.goalAnimation.isLoaded) {
        clearInterval(this.#animationInterval);
        this.#animationInterval = null;

        const canvas = goalAnimationElement.querySelector("canvas");
        if (canvas.getAttribute("width") === "0") {
          canvas.setAttribute("width", 300);
          canvas.setAttribute("height", 300);
        }

        localStorage.setItem("cartDiscountAnimationShown", true);
        this.goalAnimation.play();
      }
    }, 100);
  }

  #orchestrateModalEvent(e) {
    if (e.type === "on:modal:opened") {
      this.classList.add("is-visible");
    } else if (e.type === "on:modal:closed") {
      this.classList.remove("is-visible");
    }
  }
}

customElements.define("cart-discount-qty-bar", FreeShippingBar);
